import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  BuildingOffice2Icon,
  ChartBarIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  UserGroupIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const CaseStudies: React.FC = () => {
  const caseStudies = [
    {
      company: 'Sharma & Associates',
      location: 'Mumbai, Maharashtra',
      size: '5 CAs, 15 Staff',
      specialty: 'Corporate Tax & GST Compliance',
      challenge: 'Processing 200+ client GST returns monthly with manual data entry taking 100+ hours',
      solution: 'Implemented AICaFriend AI invoice scanner and automated GSTR generation',
      results: [
        '85% reduction in invoice processing time',
        'Increased client base from 40 to 120 GST clients',
        'Saved ₹3.5L annually on staff costs',
        'Zero late filing penalties after implementation',
      ],
      quote: 'AICaFriend transformed our GST practice. We now handle 3x more clients with the same team size.',
      author: 'CA Rajesh Sharma, Senior Partner',
    },
    {
      company: 'Patel Tax Consultants',
      location: 'Ahmedabad, Gujarat',
      size: '2 CAs, 8 Staff',
      specialty: 'SME Tax & Accounting',
      challenge: 'Clients submitting invoices late, causing return filing delays and manual data entry errors',
      solution: 'Deployed client GST upload portal with automated reminders and AI data extraction',
      results: [
        '95% on-time invoice submission rate',
        'Error rate dropped from 12% to 0.5%',
        'Processing time per client reduced from 8 hours to 45 minutes',
        'Added 50 new GST clients in 6 months',
      ],
      quote: 'The client portal is a game-changer. Our clients love how easy it is to submit invoices now.',
      author: 'CA Priya Patel, Founder',
    },
    {
      company: 'Gupta & Co. Chartered Accountants',
      location: 'Delhi NCR',
      size: '8 CAs, 25 Staff',
      specialty: 'Large Corporate GST Compliance',
      challenge: 'Managing high-volume GST compliance for 100+ corporate clients with complex invoicing',
      solution: 'Enterprise GST automation with bulk processing, reconciliation, and analytics',
      results: [
        'Process 10,000+ invoices monthly automatically',
        '92% faster month-end GST closing',
        'Reduced staff overtime by 70%',
        'Client retention improved to 98%',
      ],
      quote: 'The ROI was evident within 2 months. We scaled our practice without proportional staff increase.',
      author: 'CA Amit Gupta, Managing Partner',
    },
  ];

  const industryStats = [
    {
      icon: ClockIcon,
      stat: '80%',
      label: 'Average Time Saved on GST Work',
    },
    {
      icon: UserGroupIcon,
      stat: '2.5x',
      label: 'Increase in Client Capacity',
    },
    {
      icon: CurrencyRupeeIcon,
      stat: '₹2.5L',
      label: 'Average Annual Cost Savings',
    },
    {
      icon: ChartBarIcon,
      stat: '95%',
      label: 'Reduction in Data Errors',
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'GST Software Case Studies for CA Firms',
    description: 'Real success stories from CA firms using AI-powered GST automation. See how firms save 80% time and handle 3x more clients.',
  };

  return (
    <>
      <SEO
        title="CA Firm Case Studies - GST Software Success Stories"
        description="Real case studies from CA firms using AICaFriend GST automation. See how firms save 80% time, handle 3x more clients, and increase revenue. Free consultation available."
        keywords="ca firm case studies, gst software success stories, chartered accountant automation results, gst practice management, ca firm growth"
        canonicalUrl="https://aicafriend.com/case-studies"
        structuredData={structuredData}
      />

      <div className="bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-50 via-white to-purple-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                How CA Firms
                <span className="gradient-text"> Transform with GST Automation</span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
                Real success stories from chartered accountants who automated their GST practice.
                See how firms save 80% time, handle 3x more clients, and grow revenue with AICaFriend.
              </p>

              <Link
                to="/pricing"
                className="btn-primary px-8 py-4 text-lg font-semibold rounded-lg text-white shadow-lg inline-block"
              >
                Get Similar Results for Your Firm
              </Link>
            </div>
          </div>
        </section>

        {/* Industry Stats */}
        <section className="py-12 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
              {industryStats.map((item, index) => (
                <div key={index}>
                  <item.icon className="h-10 w-10 mx-auto mb-3 opacity-80" />
                  <div className="text-4xl md:text-5xl font-bold mb-2">{item.stat}</div>
                  <div className="text-sm md:text-base opacity-90">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16">
              {caseStudies.map((study, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-lg"
                >
                  {/* Header */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center mb-2">
                          <BuildingOffice2Icon className="h-6 w-6 mr-2" />
                          <h3 className="text-2xl font-bold">{study.company}</h3>
                        </div>
                        <p className="text-indigo-100 mb-4">{study.location} • {study.size}</p>
                        <div className="inline-block bg-white/20 px-3 py-1 rounded-full text-sm">
                          {study.specialty}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Challenge & Solution */}
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">The Challenge</h4>
                          <p className="text-gray-600">{study.challenge}</p>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-3">The Solution</h4>
                          <p className="text-gray-600">{study.solution}</p>
                        </div>
                        <div className="bg-indigo-50 border-l-4 border-indigo-600 p-4">
                          <p className="text-gray-700 italic">"{study.quote}"</p>
                          <p className="text-sm text-gray-600 mt-2">— {study.author}</p>
                        </div>
                      </div>

                      {/* Results */}
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Results Achieved</h4>
                        <div className="space-y-3">
                          {study.results.map((result, idx) => (
                            <div key={idx} className="flex items-start">
                              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mr-3 mt-0.5" />
                              <span className="text-gray-700">{result}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Common Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Common Benefits Across All CA Firms
              </h2>
              <p className="text-xl text-gray-600">
                What every firm experiences after implementing GST automation
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-4">
                  <ClockIcon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Massive Time Savings</h3>
                <p className="text-gray-600">
                  70-85% reduction in time spent on GST invoice processing and return preparation
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <UserGroupIcon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Client Base Growth</h3>
                <p className="text-gray-600">
                  Handle 2-3x more GST clients with the same team size and resources
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mb-4">
                  <ChartBarIcon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Higher Accuracy</h3>
                <p className="text-gray-600">
                  95%+ reduction in data entry errors and GST filing mistakes
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-yellow-100 text-yellow-600 rounded-lg flex items-center justify-center mb-4">
                  <CurrencyRupeeIcon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Cost Reduction</h3>
                <p className="text-gray-600">
                  ₹2-3L annual savings on staff costs and overtime expenses
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircleIcon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Zero Penalties</h3>
                <p className="text-gray-600">
                  Eliminate late filing penalties with automated reminders and faster processing
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center mb-4">
                  <UserGroupIcon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Better Client Experience</h3>
                <p className="text-gray-600">
                  Clients love the self-service portal and faster turnaround times
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your CA Practice?
            </h2>
            <p className="text-xl text-indigo-100 mb-8">
              Join these successful CA firms and start saving 80% time on GST work today
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/pricing"
                className="bg-white text-indigo-600 px-8 py-4 text-lg font-semibold rounded-lg hover:bg-gray-50 transform hover:scale-105 transition-all duration-200"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-4 text-lg font-semibold rounded-lg hover:bg-white hover:text-indigo-600 transition-all duration-200"
              >
                Request Detailed Case Studies
              </Link>
            </div>
            <p className="mt-6 text-sm text-indigo-100">
              Free consultation • Custom implementation plan • No credit card required
            </p>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore GST Automation Solutions</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/gst-automation-for-ca"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Complete GST Automation</h4>
                <p className="text-gray-600 text-sm">Learn how to automate your entire GST workflow</p>
              </Link>
              <Link
                to="/free-gst-software"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Free GST Software</h4>
                <p className="text-gray-600 text-sm">Start with our free forever plan</p>
              </Link>
              <Link
                to="/resources"
                className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-200"
              >
                <h4 className="font-semibold text-gray-900 mb-2">Free Resources</h4>
                <p className="text-gray-600 text-sm">GST guides, templates, and checklists</p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CaseStudies;
